/**
 * ⭐ **R9 N-11 — the NAME of the record a detail scope is in, published UP from the pane.**
 *
 * ## ⛔ WHY THIS EXISTS AT ALL, AND WHY NOTHING SIMPLER WORKS
 *
 * The scoped rail group needs a chip that says which record you are in — the owner's *"a nice
 * looking header in that LHS menu that shows me which app I've chosen"*, applied to a data plane.
 * For an app that is free: `AppHost` already holds the app LIST as a prop, so it resolves
 * `:appId` → `{ name, slug }` without asking anyone.
 *
 * ⛔ **There is no equivalent for a data plane, and the id is a UUID.** Measured against the dev
 * fixtures: a DP's `id` is `11111111-1111-4111-8111-111111111111` and its `name` is `dp-acme-eu`.
 * The record's name is known only after `DataPlanePane`'s own fetch — and the pane renders BELOW the
 * rail, so it cannot hand the name to it by props. ⇒ state above both, written by the one below.
 *
 * ⚠️ Three alternatives were considered and each is worse:
 *  · **Chip shows the id.** Honest, stateless, and a 36-character UUID in the nav on every DP screen.
 *  · **`AppHost` fetches the DP itself.** A second fetch of a record the pane is already fetching,
 *    on every DP navigation, to render one word of chrome.
 *  · **Rail reads a route-level loader.** This shell has no data-router; `AppHost` is a plain
 *    `<Routes>` and adding one is a different change entirely.
 *
 * ## ⛔ THE FALLBACK IS THE RECORD ID, AND IT IS THIS PANE'S OWN DOCUMENTED RULE
 *
 * `null` here does NOT mean "render nothing" — the consumer falls back to the id, exactly as
 * `DataPlanePane`'s breadcrumb and heading already do (`dp?.name ?? dpId`), *"because a skeleton
 * where a name belongs reads as a broken screen"*. So the chip reads the id for the ~200ms before
 * the fetch lands and the name for every moment after.
 *
 * ## ⛔ NOT A GENERAL-PURPOSE SHELL STORE
 *
 * One string, one writer at a time, cleared on unmount. ⚠️ Do not grow this into "state the pane
 * shares with the chrome": the reason the app scope is clean is that `AppHost` resolves it ONCE and
 * everything reads that one value, and every field added here is a second place a fact can live.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface DetailScopeName {
  readonly name: string | null;
  readonly publish: (name: string | null) => void;
}

/**
 * ⛔ **A NO-OP DEFAULT, NOT A THROW**, unlike `useAppRegistry`. Both consumers legitimately render
 * outside a provider: the rail renders on every screen including ones with no detail scope, and
 * `DataPlanePane` is rendered directly by a dozen test suites that mount the pane rather than the
 * whole shell. A throw would make the pane untestable in isolation to buy a wiring check that
 * `AppHost`'s own suite already performs by rendering both halves together.
 *
 * ⚠️ Same judgement `app-scope-context.tsx` records for `useAppScope`: *"the chrome renders BOTH
 * inside and outside an app scope, so 'no app in scope' is a legitimate state here rather than a
 * wiring error."*
 */
const DetailScopeNameCtx = createContext<DetailScopeName>({
  name: null,
  publish: () => {},
});

export function DetailScopeNameProvider({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element {
  const [name, publish] = useState<string | null>(null);
  /*
   * ⚠️ NOT memoised, deliberately. The value changes exactly when `name` changes — which is the only
   * time a consumer needs to re-render — so a `useMemo` here would add a dependency array to
   * maintain in exchange for nothing. `publish` is `useState`'s setter and is already stable.
   */
  return <DetailScopeNameCtx value={{ name, publish }}>{children}</DetailScopeNameCtx>;
}

/** The name of the record in scope, or `null` — meaning *"fall back to the id"*, never *"hide"*. */
export function useDetailScopeName(): string | null {
  return useContext(DetailScopeNameCtx).name;
}

/**
 * Publish the in-scope record's name for the chrome above.
 *
 * ⛔ **TWO EFFECTS, AND SPLITTING THEM IS THE WHOLE CORRECTNESS ARGUMENT.** The obvious single
 * effect — `useEffect(() => { publish(name); return () => publish(null); }, [name, publish])` — runs
 * its cleanup on every *change* of `name`, not only on unmount. So every rename or arrival would
 * publish `null` and then the new value: one extra render, and a one-frame flash of the raw UUID in
 * the rail on the exact transition this hook exists to smooth.
 *
 * ⇒ The second effect's dependency list holds only `publish` (a `useState` setter, stable for the
 * provider's life), so its cleanup fires **on unmount only** — which is when a stale name really
 * must go, because the rail outlives the pane.
 */
export function usePublishDetailScopeName(name: string | null): void {
  const { publish } = useContext(DetailScopeNameCtx);
  useEffect(() => {
    publish(name);
  }, [name, publish]);
  useEffect(() => () => publish(null), [publish]);
}
